import React, { useEffect, useState, useRef } from 'react';
import { setQuoteResponse } from '../../redux/actions';
import { setCreateBookingResponse } from '../../redux/actions/createBookingActions';
import { updateBookingPrice } from '../../redux/actions/bookingActions'; // Import the booking action
import { setTravelDetails } from '../../redux/actions/travelDetailsActions';
import { useDispatch } from 'react-redux';
import { setCombinations } from '../../redux/actions/mixedProductActions';
import {setPromoCode, setPromo, setCodeApplied} from "../../redux/actions/setPromoCode";

const GetBasket = ({ id, onLoadingChange, trigger }) => {
  const dispatch = useDispatch();
  const apiBaseUrlAPI1 = process.env.REACT_APP_API_BASE_URL;


  const [loading, setLoading] = useState(true);
  const lastFetchKey = useRef(null);

  useEffect(() => {

    // Only fetch when the basket (id) or the refresh trigger actually changes.
    // Guards against React StrictMode's dev double-invoke and against re-renders
    // that don't change id/trigger, which previously caused getBasket/createBasket
    // to fire several times on a single page load.
    const fetchKey = `${id}:${trigger ?? ''}`;
    if (lastFetchKey.current === fetchKey) {
      return;
    }
    lastFetchKey.current = fetchKey;

    const fetchData = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        const response = await fetch(`${apiBaseUrlAPI1}/basket/getBasket?guid=${id}`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();

        dispatch(setQuoteResponse(data));
        dispatch(setCreateBookingResponse(data));

        if(data.basket.promoCode) {
          dispatch(setPromoCode(data.basket.promoCode));
          dispatch(setPromo(true));
          dispatch(setCodeApplied(true));
        }


        // Mixed Products
        // Assume apiBaseUrlAPI1, headers, and data are already defined

        // Function to filter and sort options based on 'book: true' and non-existence in the existingTransferTypeCodes
        const filterAndSortOptions = (options) => {
          return options
            // Filter for book: true and not in existingTransferTypeCodes
            .filter(option => option.book === true)
            // Sort by the cheapest 'totalCost'
            .sort((a, b) => a.optionCost.totalCost - b.optionCost.totalCost);
        };

        const fetchOptions = async (transfers, index) => {
          const body = JSON.stringify({
            "basket": {
              "id": 0,
              "transfers": [
                {
                  "id": 0,
                  "fromDestination": { "id": transfers[index].fromDestination.id },
                  "toDestination": { "id": transfers[index].toDestination.id },
                  "date": transfers[index].date,
                  "time": transfers[index].time,
                  "adults": transfers[index].adults,
                  "numberOfChildren": transfers[index].children.length
                }
              ]
            }
          });

          const response = await fetch(`${apiBaseUrlAPI1}/basket/createBasket`, {
            method: 'POST',
            headers,
            body,
          });
          const data = await response.json();
          return filterAndSortOptions(data.basket.options);
        };

        const combineOptionsAndSort = async () => {
          if (data.basket.transfers.length > 0) {
            
        
            // Check if at least one option has book: false
            // const hasUnbookableOption = data.basket.options.some(option => option.book === false);
        
            // Fetch outbound options
            let outboundOptions = await fetchOptions(data.basket.transfers, 0);
            outboundOptions = outboundOptions.filter(option => option.transferTypeCode !== "SCHD");

            // Fetch inbound options - assuming there's a second transfer for inbound
            let inboundOptions = data.basket.transfers.length > 1 ? await fetchOptions(data.basket.transfers, 1) : [];
            inboundOptions = inboundOptions.filter(option => option.transferTypeCode !== "SCHD");

            let cheapestCombination = false;
        
            // Proceed with finding the cheapest combination
            
            outboundOptions.forEach(outbound => {
              inboundOptions.forEach(inbound => {
                if (outbound.transferTypeCode !== inbound.transferTypeCode
                    && outbound.transferTypeCode !== 'SCHD'
                    && inbound.transferTypeCode !== 'SCHD'
                ) {
                  const totalCost = outbound.optionCost.totalCost + inbound.optionCost.totalCost;
                  const costPerPerson = outbound.optionCost.costPerPerson + inbound.optionCost.costPerPerson;
                  // Check for the cheapest combination based on totalCost and then costPerPerson
                  if (!cheapestCombination || totalCost < cheapestCombination.totalCost || (totalCost === cheapestCombination.totalCost && costPerPerson < cheapestCombination.costPerPerson)) {
                    cheapestCombination = { outbound, inbound, totalCost, costPerPerson };
                  }
                }
              });
            });

            // Dispatch the action to store the cheapest combination in Redux
            if (cheapestCombination) {
              dispatch(setCombinations([cheapestCombination])); // We're sending an array with a single item again
            } else {
              // Handle the case where no valid combinations were found or no unbookable options exist
              dispatch(setCombinations([]));
            }
        
            return cheapestCombination ? [cheapestCombination] : [];
          } else {
            return [];
          }
        };

        combineOptionsAndSort();

        // Assuming data.basket.options is an array
        const options = data.basket.options;

        // Check if any option has book set to false
        const hasOptionWithBookFalse = options.some(option => option.book === true);

        // Dispatch the action if the condition is met
        if (hasOptionWithBookFalse) {
          dispatch(updateBookingPrice(0, 0, '', true, null));
        } else {
          dispatch(updateBookingPrice(0, 0, '', false, null));
        }

        const mapTransferDetails = (transfer) => {
          return {
            label: transfer.name,
            subtitle: transfer.name,
            value: transfer.destinationType,
            type: transfer.destinationTypeCode,
            id: transfer.id,
          };
        };

        // Gather form values
        const firstTransfer = data.basket.transfers[0];
        const secondTransfer = data.basket.transfers[1]; // This may or may not exist

        const formValues = {
          pickupValue: mapTransferDetails(firstTransfer.fromDestination),
          dropoffValue: mapTransferDetails(firstTransfer.toDestination),
          outboundDate: firstTransfer.date,
          outboundTime: firstTransfer.time,
          adultsCount: firstTransfer.adults, // or get it from your form
          childrenCount: firstTransfer.children,
          infantsCount: firstTransfer.children,
          returnDate: secondTransfer ? secondTransfer.date : null,
          returnTime: secondTransfer ? secondTransfer.time : null
        };

        // Dispatch the action to store the form values
        dispatch(setTravelDetails(formValues));
        onLoadingChange(false);
      } catch (error) {
        // console.error('Error fetching get basket:', error);
      } finally {
        // Set loading to false regardless of success or failure
        setLoading(false);
       
      }
    };

    fetchData();
  }, [apiBaseUrlAPI1, dispatch, id, onLoadingChange, trigger]);




  if (loading) {
    return <p>Loading...</p>; // You can replace this with your own loading indicator
  }

  // Render your actual component content
  return (
    <div>

    </div>
  );
};

export default GetBasket;
